import { Button } from '@/lib';
import { BaseModal } from '@/lib/Modals';
import { SpinnerIcon } from '@/lib/Spinner';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useDeleteDataSourceMutation, useGetDataSourceDependenciesQuery } from '@/services/dataSources';
import { useDeleteAudienceMutation, useGetAudienceDependenciesQueryQuery } from '@/services/endpoints/audiences';
import { useRef, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { ComuniList } from '../useComuniLists';

type DeleteCitizenFormDialogProps = {
  form: ComuniList;
  onClose: () => void;
  onDeleted: () => void;
};

/**
 * Deletes a citizen form: its audience first, then its datasource. Anything else using either one (a combined
 * list, the audience of a sent communication) blocks the whole delete, so a form never ends up half deleted,
 * still collecting registrations behind a QR code with no list in the tab.
 */
export const DeleteCitizenFormDialog: FC<DeleteCitizenFormDialogProps> = ({ form, onClose, onDeleted }) => {
  const { t } = useTranslation('comuni');
  const { id: workspaceId } = useWorkspace();
  const dataSourceId = form.datasources[0].id;
  // always refetched: a cached answer could miss a communication sent since the last look
  const audienceDeps = useGetAudienceDependenciesQueryQuery({ workspaceId, audienceId: form.id }, { refetchOnMountOrArgChange: true });
  const dataSourceDeps = useGetDataSourceDependenciesQuery({ workspaceId, dataSourceId }, { refetchOnMountOrArgChange: true });
  const [deleteAudience] = useDeleteAudienceMutation();
  const [deleteDataSource] = useDeleteDataSourceMutation();
  const [deleting, setDeleting] = useState(false);
  const [failed, setFailed] = useState(false);
  // a retry after a datasource failure must not try the already deleted audience again
  const audienceDeleted = useRef(false);

  const loading = audienceDeps.isFetching || dataSourceDeps.isFetching;
  const loadFailed = audienceDeps.isError || dataSourceDeps.isError;
  const usedBy = [
    ...new Map([...(audienceDeps.data?.audiences ?? []), ...(dataSourceDeps.data?.audiences ?? [])].map((audience) => [audience.id, audience])).values(),
  ].filter((audience) => audience.id !== form.id);
  const canDelete = !loading && !loadFailed && usedBy.length === 0;

  const remove = async () => {
    setDeleting(true);
    setFailed(false);
    try {
      if (!audienceDeleted.current) {
        await deleteAudience({ workspaceId, audienceId: form.id }).unwrap();
        audienceDeleted.current = true;
      }
      await deleteDataSource({ workspaceId, dataSourceId }).unwrap();
      onDeleted();
    } catch {
      setFailed(true);
      setDeleting(false);
    }
  };

  return (
    <BaseModal onClose={onClose} isDismissable={!deleting} aria-labelledby='delete-citizen-form-title'>
      <div className='flex flex-col gap-3'>
        <h3 id='delete-citizen-form-title' className='text-lg font-semibold text-primary-900'>
          {t('citizenForms.deleteTitle')}
        </h3>
        {loading ? (
          <div role='status' aria-label={t('publicForm.loading')}>
            <SpinnerIcon className='size-6 text-brand-700' />
          </div>
        ) : loadFailed ? (
          <p className='text-sm text-red-700' role='alert'>
            {t('citizenForms.dependenciesError')}
          </p>
        ) : usedBy.length ? (
          <>
            <p className='text-sm text-tertiary-600'>{t('citizenForms.inUse', { name: form.name })}</p>
            <ul className='list-disc pl-5 text-sm text-primary-900'>
              {usedBy.map((audience) => (
                <li key={audience.id}>{audience.name}</li>
              ))}
            </ul>
          </>
        ) : (
          <p className='text-sm text-tertiary-600'>{t('citizenForms.deleteWarning', { name: form.name })}</p>
        )}
        {failed && (
          <p className='text-sm text-red-700' role='alert'>
            {t('citizenForms.deleteError')}
          </p>
        )}
      </div>
      <div className='flex justify-end gap-3 pt-4'>
        <Button intent='secondary' size='lg' onClick={onClose} disabled={deleting}>
          {t(canDelete ? 'citizenForms.cancel' : 'citizenForms.close')}
        </Button>
        {canDelete && (
          <Button intent='primary' size='lg' onClick={remove} isLoading={deleting}>
            {t('citizenForms.deleteConfirm')}
          </Button>
        )}
      </div>
    </BaseModal>
  );
};
